import { useRef } from 'react'
import styled from '@emotion/styled'
import { Paperclip } from 'lucide-react'
import { IconButton } from 'components/core/IconButton'
import { SPACING } from 'constants/spacing.constants'
import { FileExtension } from 'types/file.types'
import { validateFile } from 'utils/file.utils'

const StyledInput = styled.input`
    display: none;
`

type FileAttachmentButtonProps = {
    maxFileSizeMb?: number // megabytes
    acceptedFileTypes?: FileExtension[] // e.g. ['pdf', 'csv']
    onFilesSelected: (files: File[]) => void
}

export const FileAttachmentButton = ({
    maxFileSizeMb,
    acceptedFileTypes = [],
    onFilesSelected,
}: FileAttachmentButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const _onButtonClick = () => {
        if (!fileInputRef.current) {
            return
        }

        fileInputRef.current.click()
    }

    const _onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (!files) {
            return
        }

        const validFiles = Array.from(files).filter((file) =>
            validateFile({ maxFileSizeMb, acceptedFileTypes, file })
        )
        if (validFiles.length > 0) {
            onFilesSelected(validFiles)
        }

        // Clear the input value so that the user can select the same file again if needed.
        // For example, the selection was accidentally deleted and the user wants to select the same file again.
        event.target.value = ''
    }

    return (
        <span>
            <IconButton onClick={_onButtonClick}>
                <Paperclip size={SPACING.p2} />
            </IconButton>
            <StyledInput
                type="file"
                ref={fileInputRef}
                accept={acceptedFileTypes.map((type) => `.${type}`).join(',')}
                onChange={_onInputChange}
            />
        </span>
    )
}
